// StallBulkUploadItems.tsx
import React, { useState, useEffect } from "react";
import {
  Button,
  MenuItem,
  Select,
  FormControl,
  FormHelperText,
} from "@material-ui/core";
import * as XLSX from "xlsx";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import "./StallBulkUploadItems.scss";
import { http, localstorage } from "../../fx-core";

interface CSVItem {
  itemName: string;
  description: string;
  deliveryTime: string;
  deliveryUnit: number;
  totalQuantity: string;
  price: string;
  listingPrice: string;
  unit: string;
  serviceId?: number;
  categoryId?: number;
  categoryName?: string;
  itemTagIds?: string; // comma separated IDs
  itemTagNames?: string; // comma separated Names
  error?: string;
  status?: "success" | "error";
  message?: string;
  isExisting?: boolean;
  existingItemData?: any;
}

interface Category {
  id: number;
  categoryName: string;
  serviceId: number;
}

interface Stall {
  id: number;
  text: string;
  code?: string | null;
  isDefault?: boolean;
  attributes?: any;
}

interface ItemTag {
  id: number;
  text: string;
}

const deliveryUnits = [
  { id: 1, label: "Minutes" },
  { id: 2, label: "Hours" },
  { id: 3, label: "Days" },
];

const StallBulkUploadItems: React.FC = () => {
  const [csvData, setCsvData] = useState<CSVItem[]>([]);
  const [newItems, setNewItems] = useState<CSVItem[]>([]);
  const [existingItems, setExistingItems] = useState<CSVItem[]>([]);
  const [fileName, setFileName] = useState<string>("");
  const [categories, setCategories] = useState<Category[]>([]);
  const [itemTags, setItemTags] = useState<ItemTag[]>([]);
  const [communityId] = useState<number>(
    parseInt(localstorage.getItem("communityId") || "0")
  );
  const [userId] = useState<number>(
    parseInt(localstorage.getItem("userId") || "0")
  );
  const [stalls, setStalls] = useState<Stall[]>([]);
  const [selectedStallId, setSelectedStallId] = useState<number | "">("");

  const fetchCategories = () => {
    http
      .post({ url: "category/active", data: { communityId } })
      .then((res) => {
        const activeCategories = (res.data.categories || []).filter(
          (c: any) => c.isActive
        );
        setCategories(activeCategories);
      })
      .catch((err) => console.error("Error fetching categories:", err));
  };

  const fetchItemTags = () => {
    http
      .get({ url: `itemtag/community/${communityId}` })
      .then((res) => {
        setItemTags(res.data.itemTags || []);
      })
      .catch((err) => console.error("Error fetching itemTags:", err));
  };

  const initLookup = () => {
    const inputData = {
      lookups: { ProviderStalls: { default: false } },
      filters: { communityId, userId },
    };

    http
      .post({ url: "options/lookup", data: inputData })
      .then((res) => {
        const result: Stall[] = res.data.ProviderStalls || [];
        setStalls(result);
        if (result.length > 0) setSelectedStallId(result[0].id);
      })
      .catch((err) => console.error("Error fetching stalls:", err));
  };

  useEffect(() => {
    fetchCategories();
    fetchItemTags();
    initLookup();
  }, []);

  // Download XLSX template
  const downloadTemplate = async () => {
    const workbook = new ExcelJS.Workbook();

    const instructionSheet = workbook.addWorksheet("Instructions");
    instructionSheet.addRow(["Instructions for Uploading Items"]);
    instructionSheet.addRow([]);
    instructionSheet.addRow([
      "1. Delete the example row in UploadTemplate before uploading.",
    ]);
    instructionSheet.addRow([
      "2. Fill all required fields. For Delivery Unit, Category, and ItemTag, select from dropdowns.",
    ]);
    instructionSheet.addRow([
      "3. You can select up to 3 item tags in columns itemTags1, itemTags2, itemTags3.",
    ]);

    const lookupSheet = workbook.addWorksheet("Lookups");
    deliveryUnits.forEach((du, i) => {
      lookupSheet.getCell(`A${i + 1}`).value = du.label;
    });
    categories.forEach((cat, i) => {
      lookupSheet.getCell(`B${i + 1}`).value = cat.categoryName;
    });
    itemTags.forEach((tag, i) => {
      lookupSheet.getCell(`C${i + 1}`).value = tag.text;
    });
    lookupSheet.state = "veryHidden";

    const uploadSheet = workbook.addWorksheet("UploadTemplate");
    uploadSheet.addRow([
      "itemName",
      "description",
      "deliveryTime",
      "deliveryUnit",
      "totalQuantity",
      "price",
      "listingPrice",
      "unit",
      "category",
      "itemTags1",
      "itemTags2",
      "itemTags3",
    ]);

    uploadSheet.addRow([
      "Apple",
      "Fresh red apples",
      "30",
      "Minutes",
      "100",
      "50",
      "45",
      "kg",
      categories[0]?.categoryName || "",
      itemTags[0]?.text || "",
      itemTags[1]?.text || "",
      "",
    ]);

    for (let row = 2; row <= 1000; row++) {
      uploadSheet.getCell(`D${row}`).dataValidation = {
        type: "list",
        allowBlank: false,
        formulae: [`Lookups!$A$1:$A$${deliveryUnits.length}`],
      };
      uploadSheet.getCell(`I${row}`).dataValidation = {
        type: "list",
        allowBlank: false,
        formulae: [`Lookups!$B$1:$B$${categories.length}`],
      };
      ["J", "K", "L"].forEach((col) => {
        uploadSheet.getCell(`${col}${row}`).dataValidation = {
          type: "list",
          allowBlank: true,
          formulae: [`Lookups!$C$1:$C$${itemTags.length}`],
        };
      });
    }

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), "StallBulkUploadItems.xlsx");
  };

  const checkExistingItems = async (parsedData: CSVItem[]) => {
    if (!selectedStallId) {
      return parsedData;
    }

    try {
      const stallItemsRes = await http.get({
        url: `stallitem/stall/${selectedStallId}`,
      });
      const stallItems = stallItemsRes.data || [];

      const updatedData = parsedData.map((item) => {
        const existingItem = stallItems.find(
          (si: any) =>
            si.stallItem?.itemName?.toLowerCase() === item.itemName.toLowerCase()
        );

        if (existingItem) {
          return {
            ...item,
            isExisting: true,
            existingItemData: existingItem,
          };
        }
        return {
          ...item,
          isExisting: false,
        };
      });

      return updatedData;
    } catch (err) {
      console.error("Error checking existing items:", err);
      return parsedData.map(item => ({ ...item, isExisting: false }));
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];
      setFileName(file.name);

      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: "array" });

      const sheet = workbook.Sheets["UploadTemplate"];
      if (!sheet) {
        alert("UploadTemplate sheet not found.");
        return;
      }

      const jsonData: any[] = XLSX.utils.sheet_to_json(sheet, { defval: "" });

      const parsedData = jsonData.map((item) => {
        const duLabel = (item.deliveryUnit || "").trim();
        const catLabel = (item.category || "").trim();

        const tagLabels = [item.itemTags1, item.itemTags2, item.itemTags3]
          .map((t: string) => t?.trim())
          .filter((t: string) => t);

        const du = deliveryUnits.find((d) => d.label === duLabel);
        const category = categories.find((c) => c.categoryName === catLabel);
        const tags = itemTags.filter((tag) => tagLabels.includes(tag.text));

        const tagIds =
          tags.length > 0 ? tags.map((t) => t.id).join(",") : undefined;
        const tagNames =
          tags.length > 0 ? tags.map((t) => t.text).join(", ") : "you didn't select";

        return {
          ...item,
          deliveryUnit: du?.id || 1,
          categoryId: category?.id,
          categoryName: category?.categoryName,
          serviceId: category?.serviceId,
          itemTagIds: tagIds,
          itemTagNames: tagNames,
          error: !category ? "Invalid category" : "",
        };
      }) as CSVItem[];

      // Check for existing items
      const updatedData = await checkExistingItems(parsedData);
      
      // Separate new and existing items
      const newItemsList = updatedData.filter(item => !item.isExisting);
      const existingItemsList = updatedData.filter(item => item.isExisting);

      setCsvData(updatedData);
      setNewItems(newItemsList);
      setExistingItems(existingItemsList);
    }
  };

  // Handle submit for new items
  const handleSubmitNewItems = async () => {
    if (!selectedStallId) {
      alert("Please select a stall before submitting.");
      return;
    }

    let hasError = false;
    const validatedData = newItems.map((row) => {
      if (!row.categoryId || !row.serviceId) {
        hasError = true;
        return { ...row, error: "Category required" };
      }
      return { ...row, error: "" };
    });

    setNewItems(validatedData);

    if (hasError) {
      alert("Please select category for all products.");
      return;
    }

    const today = new Date().toISOString().split("T")[0];

    try {
      const payload = {
        ServiceRequest: {
          serviceId: validatedData[0].serviceId,
          communityId,
          serviceRequestType: "Sell",
          stallId: selectedStallId,
          currentDate: today,
        },
        ServiceRequestDetails: validatedData.map((row) => ({
          itemId: -1,
          itemName: row.itemName,
          categoryId: row.categoryId,
          categoryName: row.categoryName,
        })),
      };

      const res = await http.post({ url: "servicerequest/save", data: payload });
      if (!res.data?.serviceReqId) throw new Error("Unexpected response");

      // Update the status for new items
      const updatedNewItems = validatedData.map(row => ({
        ...row,
        status: "success" as "success",
        message: "New item uploaded successfully"
      }));

      setNewItems(updatedNewItems);

      // Update the main csvData as well
      const updatedCsvData = csvData.map(item => {
        if (!item.isExisting && updatedNewItems.find(ni => ni.itemName === item.itemName)) {
          const newItemData = updatedNewItems.find(ni => ni.itemName === item.itemName);
          return { ...item, ...newItemData };
        }
        return item;
      });
      setCsvData(updatedCsvData);

    } catch (err: any) {
      console.error("Upload error:", err);
      const updated = validatedData.map((row) => ({
        ...row,
        status: "error" as "error",
        message: err?.response?.data?.message || "Failed to upload new item",
      }));
      setNewItems(updated);
    }
  };

  // Handle update for existing items
  const handleUpdateExistingItems = async () => {
    if (!selectedStallId) {
      alert("Please select a stall before updating.");
      return;
    }

    let hasError = false;
    const validatedData = existingItems.map((row) => {
      if (!row.categoryId || !row.serviceId) {
        hasError = true;
        return { ...row, error: "Category required" };
      }
      return { ...row, error: "" };
    });

    setExistingItems(validatedData);

    if (hasError) {
      alert("Please select category for all products.");
      return;
    }

    const today = new Date().toISOString().split("T")[0];

    try {
      for (const row of validatedData) {
        try {
          const existingItemData = row.existingItemData;
          
          // Update stallitem
          const putPayload = {
            id: existingItemData.stallItem.id,
            itemName: row.itemName,
            description: row.description,
            itemTags: row.itemTagIds || "",
            communityId,
            deliveryTime: Number(row.deliveryTime) || 0,
            deliveryTimeUnits: row.deliveryUnit || 1,
            isActive: true,
            isTreasureHunt: 0,
          };

          await http.put({ url: "stallitem/save", data: putPayload });

          // Save stallitembatch
          await http.post({
            url: "stallitembatch/save",
            data: {
              id: 0,
              stallItemId: existingItemData.stallItem.id,
              totalQuantity: Number(row.totalQuantity) || 0,
              price: Number(row.price) || 0,
              listingPrice: Number(row.listingPrice) || 0,
              expiryTime: 0,
              unit: row.unit,
              itemName: row.itemName,
              itemId: existingItemData.stallItem.itemId,
              stallId: selectedStallId,
              serviceId: row.serviceId,
              currentDate: today,
              isActive: true,
            },
          });

          row.status = "success";
          row.message = "Updated & Batch saved";
        } catch {
          row.status = "error";
          row.message = "Failed to update/batch save";
        }
      }

      setExistingItems([...validatedData]);

      // Update the main csvData as well
      const updatedCsvData = csvData.map(item => {
        if (item.isExisting && validatedData.find(ei => ei.itemName === item.itemName)) {
          const existingItemData = validatedData.find(ei => ei.itemName === item.itemName);
          return { ...item, ...existingItemData };
        }
        return item;
      });
      setCsvData(updatedCsvData);

    } catch (err: any) {
      console.error("Update error:", err);
      const updated = validatedData.map((row) => ({
        ...row,
        status: "error" as "error",
        message: err?.response?.data?.message || "Failed to update existing item",
      }));
      setExistingItems(updated);
    }
  };

  const renderTable = (data: CSVItem[], title: string) => {
    if (data.length === 0) return null;

    return (
      <div className="csv-preview">
        <h3>{title}</h3>
        <table>
          <thead>
            <tr>
              <th>Item Name</th>
              <th>Description</th>
              <th>Delivery Time</th>
              <th>Delivery Unit</th>
              <th>Total Quantity</th>
              <th>Price</th>
              <th>Listing Price</th>
              <th>Unit</th>
              <th>Category</th>
              <th>Item Tags</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {data.map((row, i) => (
              <tr key={i}>
                <td>{row.itemName}</td>
                <td>{row.description}</td>
                <td>{row.deliveryTime}</td>
                <td>
                  {
                    deliveryUnits.find((du) => du.id === row.deliveryUnit)
                      ?.label
                  }
                </td>
                <td>{row.totalQuantity}</td>
                <td>{row.price}</td>
                <td>{row.listingPrice}</td>
                <td>{row.unit}</td>
                <td>
                  {row.categoryName || (
                    <span style={{ color: "red" }}>Invalid</span>
                  )}
                </td>
                <td>{row.itemTagNames}</td>
                <td>
                  {row.status === "success" && (
                    <span style={{ color: "green" }}>✔ {row.message}</span>
                  )}
                  {row.status === "error" && (
                    <span style={{ color: "red" }}>✖ {row.message}</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="stall-bulk-upload-container">
      <div
        className="top-bar"
        style={{ display: "flex", justifyContent: "space-between" }}
      >
        <Button variant="contained" color="primary" onClick={downloadTemplate}>
          Download XLSX Template
        </Button>

        <FormControl style={{ minWidth: 200 }}>
          <Select
            value={selectedStallId || ""}
            onChange={(e) => setSelectedStallId(Number(e.target.value))}
          >
            {stalls.map((stall) => (
              <MenuItem key={stall.id} value={stall.id}>
                {stall.text}
              </MenuItem>
            ))}
          </Select>
          <FormHelperText>Select Stall</FormHelperText>
        </FormControl>
      </div>

      <div className="upload-area">
        <input
          type="file"
          accept=".xlsx"
          id="xlsxUpload"
          style={{ display: "none" }}
          onChange={handleFileUpload}
        />
        <label htmlFor="xlsxUpload">
          <Button variant="contained" color="secondary" component="span">
            Upload XLSX
          </Button>
        </label>
        {fileName && <p>Uploaded File: {fileName}</p>}
      </div>

      {/* New Items Table */}
      {renderTable(newItems, "New Items")}
      {newItems.length > 0 && (
        <div style={{ marginTop: "10px", marginBottom: "20px" }}>
          <Button variant="contained" color="primary" onClick={handleSubmitNewItems}>
            Submit New Items
          </Button>
        </div>
      )}

      {/* Existing Items Table */}
      {renderTable(existingItems, "Existing Items")}
      {existingItems.length > 0 && (
        <div style={{ marginTop: "10px", marginBottom: "20px" }}>
          <Button variant="contained" color="secondary" onClick={handleUpdateExistingItems}>
            Update Existing Items
          </Button>
        </div>
      )}
    </div>
  );
};

export default StallBulkUploadItems;